import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Injector, signal } from '@angular/core';
import AsyncLock from 'async-lock';
import { firstValueFrom, timer } from 'rxjs';
import { AppContext } from 'src/app/app/context';
import { logger } from 'src/app/pbir/utils/logger';
import {
    DataQueryService
} from 'src/app/services/api.services';
import { VisualDataRequest } from '../VisualDataRequest';
import {
    VisualDataQuery,
    VisualDataQueryStatus,
    VisualDataSource
} from '../VisualDataSource';
import { DataQueryCache } from './DataQuery/DataQueryCache';
import { QueryImpl, QueryState } from './DataQuery/QueryImpl';
import { ResourceManagerImpl } from './DataQuery/ResourceManagerImpl';
import { TableImpl } from './DataQuery/TableImpl';
import { isCanvasRendererHost } from 'src/app/canvas-renderer/canvas-renderer-host';

const LOGGER = logger('DataQueryVisualDataSource');

@Injectable({ providedIn: 'root' })
export class DataQueryVisualDataSource extends VisualDataSource {
  readonly injector = inject(Injector);
  readonly appContext = inject(AppContext);

  private readonly _cache = inject(DataQueryCache);

  get disableCache() {
    if (isCanvasRendererHost)
      return true;

    return !!this.appContext.features()?.dataQuery.disableClientSideCache;
  }

  get cache() {
    if (this.disableCache)
      return DataQueryCache.none;

    return this._cache;
  }

  readonly dataQueryService = inject(DataQueryService);
  readonly httpClient = inject(HttpClient);

  readonly rowsPerChunk = 500;

  readonly localQueries = new Map<string, QueryImpl>();
  readonly localQueriesLock = new AsyncLock();

  constructor() {
    super();
  }

  protected queryImpl(request: VisualDataRequest): VisualDataQuery {
    return new QueryImplRef(this, request);
  }

  async getOrAddLocalQueryImpl(request: VisualDataRequest) {
    return await this.localQueriesLock.acquire(request.cacheKey, async () => {
      let impl = this.localQueries.get(request.cacheKey);

      let valid = !!impl && !impl.failure() && !impl.aborted();
      if (valid) 
        valid = await this.cache.hasQuery(request);

      if (valid && impl && impl.id()) {
        try {
          let isValid = await firstValueFrom(this.dataQueryService.isQueryValid(impl.id()));
          if (!isValid)
            valid = false;
        }
        catch (err) {
          LOGGER.error(`Unexpected error`, err);
          valid = false; 
        }
      }

      if (!valid) {
        impl = new QueryImpl(this, request, request.debugName);
        this.localQueries.set(request.cacheKey, impl);
      }

      let cachedQuery = await this.cache.getOrAddQuery(request);
      return { impl, cachedQuery };
    });
  }
}

class QueryImplRef implements VisualDataQuery {
  private readonly defaultQueryState = {
    timestamp: new Date(),
    status: VisualDataQueryStatus.Requested,
  };

  readonly cachedQuery = signal<DataQueryCache.CachedQuery>({
    status: DataQueryCache.CachedQueryStatus.AddedNew,
    owned: false,
    state: this.defaultQueryState
  });

  readonly impl = signal<QueryImpl>(null);

  constructor(
    public readonly dataSource: DataQueryVisualDataSource,
    public readonly request: VisualDataRequest) {
    timer(0).subscribe(async () => {
      let { impl, cachedQuery } = await this.dataSource.getOrAddLocalQueryImpl(this.request);
      
      if (!this.aborted())
        impl.addRef();

      this.cachedQuery.set(cachedQuery);
      this.impl.set(impl);
    });
  }

  get injector() { return this.dataSource.injector; }
  get cache() { return this.dataSource.cache; }
  get dataQueryService() { return this.dataSource.dataQueryService; }
  get httpClient() { return this.dataSource.httpClient; }

  readonly state = computed<QueryState>(() => {
    let cachedQuery = this.cachedQuery();
    let impl = this.impl();

    if (cachedQuery) {
      if (cachedQuery.owned && impl)
        return impl.queryState();
      else
        return cachedQuery.state ?? this.defaultQueryState;
    }

    return this.defaultQueryState;
  });

  readonly resources = new ResourceManagerImpl();

  readonly status = computed(() => this.state()?.status);
  readonly id = computed(() => this.state()?.queryId);

  readonly requestedAt = computed(() => this.state()?.requestedAt);

  readonly executingAt = computed(() => this.state()?.executingAt);
  readonly executing = computed(() => this.status() == VisualDataQueryStatus.Executing);

  readonly completedAt = computed(() => this.state()?.completedAt);
  readonly completed = computed(() => this.status() == VisualDataQueryStatus.Success
                                   || this.status() == VisualDataQueryStatus.Failure);

  readonly success = computed(() => this.status() == VisualDataQueryStatus.Success);
  readonly failure = computed(() => this.status() == VisualDataQueryStatus.Failure);

  readonly error = computed(() => this.state().error);

  readonly data = computed(() => {
    let tables = this.tables();
    return tables?.length ? tables[0] : undefined;
  });

  readonly tables = computed(() => {
    let impl = this.impl();
    if (impl)
      return impl.tables();

    let { queryId, elements } = this.state();
    
    return this.state()?.tables?.map((t, i) => new TableImpl(
      this.request, 
      this.dataSource,
      this.resources,
      queryId,
      elements,
      t.resultSetIndex,
      t.resultSet
    ));
  });


  readonly aborted = signal<boolean>(false);

  abort(): void {
    if (this.aborted())
      return;

    this.aborted.set(true);

    this.impl()?.dropRef({ cancelImmediately: this.dataSource.disableCache });
  }
}
